import { Fragment, useMemo, useState } from "react";
import { entryMeasure, hasVariants, MAX_REST_SEC, setFields, type Exercise } from "../core/exercises";
import { bestSet, exerciseEntries, lastTimedDuration } from "../core/progress";
import { lastSessionSets, previousSet, sameVariant, type Session, type SessionSet, type SetValues } from "../core/sessions";
import { sensorOn, type RepSensorSettings } from "../core/repSensor";
import type { Backend } from "../core/storage";
import { SET_TYPES, setLabels, type Template } from "../core/templates";
import { formatNumber, parseDistance, parseReps, parseWeight } from "../core/units";
import {
  addToTemplate, addWorkoutExercises, addWorkoutSet, adjustRest, canInterval, copyPrevious, countSets, DEFAULT_SET_SEC,
  entryExercise, missingForTick, removeWorkoutExercise, removeWorkoutSet, REST_STEP_SEC, skipRest, startTimer,
  switchWorkoutMeasure, templateUpdate, toggleSet,
  updateWorkoutExercise, updateWorkoutSet, type Workout, type WorkoutExercise, type WorkoutSet,
} from "../core/workout";
import { ActionSheet } from "./ActionSheet";
import { ConfirmDialog } from "./ConfirmDialog";
import { DurationPicker } from "./DurationPicker";
import { ExercisePicker } from "./ExercisePicker";
import { NumField } from "./NumField";
import { platform } from "../platform";
import { SensorFix } from "./SensorFix";
import { variantName } from "./HistoryTab";
import { variantAction } from "./TemplateEditor";
import { TimerScreen, timerScreenShown } from "./TimerScreen";
import { formatClock, formatDate, formatDuration, formatSet, t } from "./texts";

type Apply = (fn: (w: Workout, now: number) => Workout) => void;
type Missing = { setKey: string; fields: (keyof SetValues)[] };
type Dialog = "nothing" | "unfinished" | "update" | "discard";

/**
 * The running workout as Strong's log: workout clock and "Beenden" on top, one card per exercise with the set table
 * `Satz | Vorher | values | ✓`, "+ Satz", "Übung hinzufügen" at the bottom, and the rest timer in a bar above the
 * tabs. Ticking a set starts the rest; ▶ on a timed or hold set opens the full-screen countdown, ⏱ on a distance set
 * the stopwatch.
 */
export function WorkoutScreen(props: {
  db: Backend;
  workout: Workout;
  now: number;
  exercises: Exercise[];
  templates: Template[];
  sessions: Session[];
  apply: Apply;
  /** reloads glossary and templates (an exercise was created, or added to the template) */
  onDataChanged(): Promise<void>;
  onFinish(templateUpdate: Template | undefined): void;
  onDiscard(): void;
  /** "Reps per Sensor zählen" per exercise (todo 8); undefined while loading */
  sensor: RepSensorSettings | undefined;
  onSensor(exerciseId: string, on: boolean): Promise<boolean>;
}) {
  const { workout: w, now, apply, exercises } = props;
  const [picking, setPicking] = useState(false);
  const [setMenu, setSetMenu] = useState<{ exKey: string; setKey: string }>();
  const [exMenu, setExMenu] = useState<string>();
  const [restPicker, setRestPicker] = useState<string>();
  const [timePicker, setTimePicker] = useState<{ exKey: string; setKey: string }>();
  const [confirmRemove, setConfirmRemove] = useState<string>();
  const [missing, setMissing] = useState<Missing>();
  const [dialog, setDialog] = useState<Dialog>();
  const [update, setUpdate] = useState<Template>();
  const byId = new Map(exercises.map((e) => [e.id, e]));
  const exOf = (key: string | undefined) => w.exercises.find((x) => x.key === key);
  const template = props.templates.find((x) => x.id === w.templateId);
  // the history of an entry: the same exercise in the same variant (todo 16)
  const variantKey = (x: WorkoutExercise) => {
    const g = byId.get(x.exerciseId);
    return `${x.exerciseId} ${g ? entryMeasure(g, x) : ""}`;
  };
  const historyOf = (x: WorkoutExercise) => {
    const g = byId.get(x.exerciseId);
    return g ? sameVariant(g, entryMeasure(g, x)) : x.exerciseId;
  };
  // "Zuletzt …" per exercise: the best set of the last time (the screen renders every 200 ms, the history rarely changes)
  const variants = [...new Set(w.exercises.map(variantKey))].join("|");
  const lastTime = useMemo(() => {
    const out = new Map<string, string>();
    for (const x of w.exercises) {
      const key = variantKey(x);
      if (out.has(key)) continue;
      const e = entryExercise(props.exercises, x);
      const last = exerciseEntries(props.sessions, historyOf(x)).pop();
      const best = last && e && bestSet(last.sets, e);
      if (last && best) out.set(key, t.progress.lastTime(formatDate(last.endedAt), formatSet(best, e)));
    }
    return out;
    // recomputed when an exercise or its variant changes, not on every tick
  }, [props.sessions, props.exercises, variants]);

  const askFinish = () => {
    if (countSets(w, true) === 0) setDialog("nothing");
    else if (countSets(w, false) > 0) setDialog("unfinished");
    else askUpdate();
  };
  const askUpdate = () => {
    const u = template && templateUpdate(template, w, exercises);
    if (u) { setUpdate(u); setDialog("update"); }
    else { setDialog(undefined); props.onFinish(undefined); }
  };

  if (picking) {
    return (
      <ExercisePicker db={props.db} exercises={exercises} onCreated={props.onDataChanged} onCancel={() => setPicking(false)}
        onAdd={async (list) => {
          setPicking(false);
          const inTemplate = await addToTemplate(props.db, w.templateId, list);
          apply((v) => addWorkoutExercises(v, list));
          if (inTemplate) await props.onDataChanged();
        }} />
    );
  }

  const menuEx = exOf(setMenu?.exKey);
  const menuSetIndex = menuEx?.sets.findIndex((s) => s.key === setMenu?.setKey) ?? -1;
  const menuE = menuEx && entryExercise(exercises, menuEx);
  const restEx = exOf(restPicker);
  const timeEx = exOf(timePicker?.exKey);
  const timeSet = timeEx?.sets.find((s) => s.key === timePicker?.setKey);
  const removeEx = exOf(confirmRemove);
  const rest = w.rest;
  const overlay = timerScreenShown(w);
  /** "Reps per Sensor zählen": for timed entries, on phones */
  const sensorMenu = (x: WorkoutExercise) => {
    const e = entryExercise(exercises, x);
    if ((e?.measure !== "timed" && e?.measure !== "reps") || !props.sensor || !platform.motionLikely()) return [];
    const on = sensorOn(props.sensor, x.exerciseId);
    return [{ label: t.sensor.menu(on), onClick: () => { setExMenu(undefined); void props.onSensor(x.exerciseId, !on); } }];
  };

  return (
    <>
      <header className="workout-bar">
        <div className="workout-clock">
          <span className="clock">{formatClock(now - w.startedAt)}</span>
          <span className="sub">{w.name}</span>
        </div>
        <button className="primary" onClick={askFinish}>{t.run.finish}</button>
      </header>

      {w.exercises.map((x) => (
        <WorkoutCard key={x.key} x={x} e={entryExercise(exercises, x)} exercises={exercises} apply={apply}
          sensor={!!props.sensor && sensorOn(props.sensor, x.exerciseId)}
          previous={lastSessionSets(props.sessions, historyOf(x))} lastTime={lastTime.get(variantKey(x))}
          missing={missing} onMissing={setMissing}
          onMenu={() => setExMenu(x.key)}
          onSetMenu={(setKey) => setSetMenu({ exKey: x.key, setKey })}
          onPickTime={(setKey) => setTimePicker({ exKey: x.key, setKey })} />
      ))}

      <button className="secondary wide" onClick={() => setPicking(true)}>{t.run.addExercise}</button>
      <label className="label">{t.run.notes}
        <textarea className="field" rows={2} value={w.notes} placeholder={t.run.notesPlaceholder}
          onChange={(ev) => { const notes = ev.target.value; apply((v) => ({ ...v, notes })); }} />
      </label>
      <button className="danger wide" onClick={() => setDialog("discard")}>{t.run.discard}</button>
      {rest && !overlay && <div className={"rest-bar-space" + (exOf(rest.exKey)?.sets.find((s) => s.key === rest.setKey)?.sensor ? " tall" : "")} />}

      {rest && !overlay && <SensorFix workout={w} exercises={exercises} apply={apply} />}
      {rest && !overlay && (
        <div className="rest-bar" role="timer">
          <div className="rest-progress" style={{ width: `${Math.min(100, (100 * Math.max(0, rest.endsAt - now)) / Math.max(1, rest.endsAt - rest.startedAt))}%` }} />
          <span className="rest-time">{t.run.restLabel} <b>{formatDuration(Math.ceil(Math.max(0, rest.endsAt - now) / 1000))}</b></span>
          <button className="secondary" onClick={() => apply((v, n) => adjustRest(v, -REST_STEP_SEC, n))}>−{REST_STEP_SEC}</button>
          <button className="secondary" onClick={() => apply((v, n) => adjustRest(v, REST_STEP_SEC, n))}>+{REST_STEP_SEC}</button>
          <button className="secondary" onClick={() => apply((v, n) => skipRest(v, exercises, n))}>{t.run.skip}</button>
        </div>
      )}
      {overlay && <TimerScreen workout={w} now={now} exercises={exercises} apply={apply} />}

      {menuEx && menuSetIndex >= 0 && setMenu && (
        <ActionSheet title={`${t.template.colSet} ${setLabels(menuEx.sets)[menuSetIndex]}`} onClose={() => setSetMenu(undefined)}
          actions={[
            ...SET_TYPES.map((type) => ({
              label: (menuEx.sets[menuSetIndex].type === type ? "✓ " : "") + t.setType[type],
              onClick: () => { apply((v) => updateWorkoutSet(v, menuEx.key, setMenu.setKey, { type }, menuE)); setSetMenu(undefined); },
            })),
            ...(!menuE || setFields(menuE).duration ? [{
              label: t.run.changeTime, onClick: () => { setTimePicker(setMenu); setSetMenu(undefined); },
            }] : []),
            { label: t.template.removeSet, danger: true,
              onClick: () => { apply((v) => removeWorkoutSet(v, menuEx.key, setMenu.setKey)); setSetMenu(undefined); } },
          ]} />
      )}
      {exMenu && exOf(exMenu) && (
        <ActionSheet title={byId.get(exOf(exMenu)!.exerciseId)?.name ?? t.template.unknownExercise} onClose={() => setExMenu(undefined)}
          children={hasVariants(byId.get(exOf(exMenu)!.exerciseId)) && exOf(exMenu)!.sets.some((s) => s.done)
            ? <p className="muted-text">{t.run.variantLocked}</p> : undefined}
          actions={[
            { label: t.run.changeRest(formatDuration(exOf(exMenu)!.restSec)), onClick: () => { setRestPicker(exMenu); setExMenu(undefined); } },
            ...variantAction(byId.get(exOf(exMenu)!.exerciseId), exOf(exMenu)!, (to, e) => {
              const sec = lastTimedDuration(props.sessions, e) ?? DEFAULT_SET_SEC;
              apply((v) => switchWorkoutMeasure(v, exercises, exMenu, to, sec));
              setExMenu(undefined);
            }, exOf(exMenu)!.sets.some((s) => s.done)),
            ...sensorMenu(exOf(exMenu)!),
            { label: t.run.removeExercise, danger: true, onClick: () => {
              const x = exOf(exMenu)!;
              setExMenu(undefined);
              if (x.sets.some((s) => s.done)) setConfirmRemove(x.key);
              else apply((v) => removeWorkoutExercise(v, x.key));
            } },
          ]} />
      )}
      {restEx && (
        <DurationPicker title={t.template.rest} value={restEx.restSec} fallback={restEx.restSec} onCancel={() => setRestPicker(undefined)}
          onPick={(sec) => { apply((v) => updateWorkoutExercise(v, restEx.key, { restSec: Math.min(MAX_REST_SEC, sec ?? 0) })); setRestPicker(undefined); }} />
      )}
      {timeEx && timeSet && (
        <DurationPicker title={`${t.template.colDuration} · ${t.template.colSet} ${setLabels(timeEx.sets)[timeEx.sets.indexOf(timeSet)]}`}
          value={timeSet.durationSec} fallback={timeSet.goal?.durationSec ?? DEFAULT_SET_SEC} onCancel={() => setTimePicker(undefined)}
          onPick={(sec) => {
            apply((v) => updateWorkoutSet(v, timeEx.key, timeSet.key, { durationSec: sec }, entryExercise(exercises, timeEx)));
            setTimePicker(undefined);
          }} />
      )}
      {removeEx && (
        <ConfirmDialog text={t.run.confirmRemoveExercise(byId.get(removeEx.exerciseId)?.name ?? t.template.unknownExercise)}
          confirm={t.run.removeExercise} danger onCancel={() => setConfirmRemove(undefined)}
          onConfirm={() => { apply((v) => removeWorkoutExercise(v, removeEx.key)); setConfirmRemove(undefined); }} />
      )}

      {dialog === "nothing" && (
        <ConfirmDialog text={t.run.nothingDone} confirm={t.run.discardYes} cancel={t.run.back} danger
          onCancel={() => setDialog(undefined)} onConfirm={props.onDiscard} />
      )}
      {dialog === "discard" && (
        <ConfirmDialog text={t.run.confirmDiscard} confirm={t.run.discardYes} cancel={t.run.back} danger
          onCancel={() => setDialog(undefined)} onConfirm={props.onDiscard} />
      )}
      {dialog === "unfinished" && (
        <ConfirmDialog text={t.run.unfinished(countSets(w, false))} confirm={t.run.finishAnyway} cancel={t.run.back}
          onCancel={() => setDialog(undefined)} onConfirm={askUpdate} />
      )}
      {dialog === "update" && template && (
        <ConfirmDialog text={t.run.updateTemplate(template.name)} confirm={t.run.updateYes} cancel={t.run.updateNo}
          onCancel={() => { setDialog(undefined); props.onFinish(undefined); }}
          onConfirm={() => { setDialog(undefined); props.onFinish(update); }} />
      )}
    </>
  );
}

/** "Vorher" in a few characters: 60×8, 0:45, 20 m 0:32. */
function formatPrevious(p: SessionSet, e: Exercise | undefined): string {
  const weight = p.weightKg !== undefined && e?.equipment !== "bodyweight" ? `${formatNumber(p.weightKg)}×` : "";
  const time = p.durationSec !== undefined ? formatDuration(p.durationSec) : "";
  switch (e?.measure) {
    case "hold": return weight + time;
    case "distance": return [p.distanceM !== undefined ? `${formatNumber(p.distanceM)} m` : "", time].filter(Boolean).join(" ");
    default: return weight + (p.reps ?? "–");
  }
}

function WorkoutCard(props: {
  x: WorkoutExercise;
  e: Exercise | undefined;
  exercises: Exercise[];
  previous: SessionSet[] | undefined;
  /** "Zuletzt 10.10.2026: 60 kg × 8" */
  lastTime?: string;
  /** the sensor counts the reps of its timed sets */
  sensor: boolean;
  apply: Apply;
  missing: Missing | undefined;
  onMissing(m: Missing | undefined): void;
  onMenu(): void;
  onSetMenu(setKey: string): void;
  onPickTime(setKey: string): void;
}) {
  const { x, e, apply } = props;
  // an exercise missing from the glossary (cannot happen through the UI) shows all columns
  const f = e ? setFields(e) : { weight: true, reps: true, duration: true, distance: true };
  const labels = setLabels(x.sets);
  const prevOf = (i: number) => previousSet(props.previous, x.sets, i);
  const isMissing = (s: WorkoutSet, k: keyof SetValues) => props.missing?.setKey === s.key && props.missing.fields.includes(k);
  const change = (s: WorkoutSet, patch: Partial<WorkoutSet>) => {
    if (props.missing?.setKey === s.key) props.onMissing(undefined);
    apply((v) => updateWorkoutSet(v, x.key, s.key, patch, e));
  };
  const tick = (s: WorkoutSet, i: number) => {
    const p = prevOf(i);
    const m = s.done ? [] : missingForTick(s, e, p);
    props.onMissing(m.length ? { setKey: s.key, fields: m } : undefined);
    if (!m.length) apply((v, n) => toggleSet(v, props.exercises, x.key, s.key, n, p));
  };

  type Column = { key: string; header: string; cell(s: WorkoutSet, i: number): React.ReactNode };
  const num = (k: "weightKg" | "reps" | "distanceM", header: string, mode: "decimal" | "numeric", parse: (s: string) => number | undefined): Column => ({
    key: k, header,
    cell: (s, i) => <NumField label={header} value={s[k]} mode={mode} parse={parse} format={formatNumber}
      placeholder={formatNumber(prevOf(i)?.[k])} missing={isMissing(s, k)} onChange={(v) => change(s, { [k]: v })} />,
  });
  const stopwatch = e?.measure === "distance";
  const time: Column = {
    key: "time", header: t.template.colDuration,
    cell: (s) => {
      const value = s.durationSec !== undefined ? formatDuration(s.durationSec) : "";
      // an open countdown shows what it will count down from
      const shown = value || (stopwatch ? "" : formatDuration(DEFAULT_SET_SEC));
      if (s.done) return <button className="field num time-cell" aria-label={t.run.changeTime} onClick={() => props.onPickTime(s.key)}>{value || "–"}</button>;
      return (
        <button className={"field num go" + (isMissing(s, "durationSec") ? " invalid" : "")}
          aria-label={stopwatch ? t.run.stopwatch : t.run.startTimer}
          onClick={() => { props.onMissing(undefined); apply((v, n) => startTimer(v, props.exercises, x.key, s.key, n)); }}>
          {stopwatch ? "⏱" : "▶"}{shown && <span>{shown}</span>}
        </button>
      );
    },
  };
  // a rep set counted by the sensor (todo 17): ▶ opens the counter; typing the reps and ✓ work as before
  const sensorStart: Column = {
    key: "sensor", header: t.sensor.colStart,
    cell: (s, i) => s.done ? <span /> : (
      <button className="field num go" aria-label={t.sensor.startSet}
        onClick={() => { props.onMissing(undefined); apply((v, n) => startTimer(v, props.exercises, x.key, s.key, n, true, prevOf(i))); }}>▶</button>
    ),
  };
  const weight = num("weightKg", t.template.colWeight, "decimal", parseWeight);
  const reps = num("reps", t.template.colReps, "numeric", parseReps);
  const distance = num("distanceM", t.template.colDistance, "decimal", parseDistance);
  // a timed set: the time, then the reps done in it
  const columns = [
    f.weight && weight, f.distance && distance,
    ...(e?.measure === "timed" ? [time, reps] : [f.reps && reps, f.duration && time]),
    props.sensor && e?.measure === "reps" && sensorStart,
  ].filter((c): c is Column => !!c);
  const grid = { gridTemplateColumns: `36px minmax(0, 1.15fr) repeat(${columns.length}, minmax(0, 1fr)) 48px` };

  return (
    <section className="ex-card">
      <header className="ex-head">
        <div className="ex-title">
          <div className="name">{variantName(e?.name, x.measure !== props.exercises.find((g) => g.id === x.exerciseId)?.measure ? x.measure : undefined)}</div>
          <div className="sub">{t.run.restOf(formatDuration(x.restSec))}</div>
          {props.lastTime && <div className="sub">{props.lastTime}</div>}
          {props.sensor && (e?.measure === "timed" || e?.measure === "reps") && <div className="sub">{t.sensor.cardHint}</div>}
        </div>
        {canInterval(e) && (
          <button className={"chip" + (x.interval ? " on" : "")} aria-pressed={x.interval}
            onClick={() => apply((v) => updateWorkoutExercise(v, x.key, { interval: !x.interval }))}>
            {x.interval ? t.run.intervalOn : t.run.intervalOff}
          </button>
        )}
        <button className="secondary icon" aria-label={t.run.exerciseMenu} onClick={props.onMenu}>⋯</button>
      </header>

      <div className="set-grid head" style={grid}>
        <span>{t.template.colSet}</span>
        <span>{t.run.colPrevious}</span>
        {columns.map((c) => <span key={c.key}>{c.header}</span>)}
        <span>{t.run.colDone}</span>
      </div>
      {x.sets.length === 0 && <p className="muted-text">{t.run.noSets}</p>}
      {x.sets.map((s, i) => {
        const p = prevOf(i);
        return (
          <div key={s.key} className={"set-grid run" + (s.done ? " done" : "")} style={grid}>
            <button className={"set-label " + s.type} onClick={() => props.onSetMenu(s.key)}>{labels[i]}</button>
            <button className="prev" disabled={!p} onClick={() => {
              if (!p) return;
              if (props.missing?.setKey === s.key) props.onMissing(undefined);
              apply((v) => copyPrevious(v, x.key, s.key, e, p));
            }}>
              {p ? formatPrevious(p, e) : "–"}
            </button>
            {columns.map((c) => <Fragment key={c.key}>{c.cell(s, i)}</Fragment>)}
            <button className={"tick" + (s.done ? " on" : "")} aria-label={t.run.tick} aria-pressed={s.done} onClick={() => tick(s, i)}>✓</button>
          </div>
        );
      })}
      {props.missing && x.sets.some((s) => s.key === props.missing?.setKey) && <p className="error">{t.run.missingValues}</p>}
      <button className="secondary add-set" onClick={() => apply((v) => addWorkoutSet(v, x.key, e))}>{t.template.addSet}</button>
    </section>
  );
}
